import React from 'react';
import { Badge, Box, Typography } from '@mui/material';

export const ACCENT = '#2099d2';
export const ACCENT_LIGHT = '#2099d214';
export const BORDER = '#e2e8f0';
export const TEXT_PRIMARY = '#334155';
export const TEXT_SECONDARY = '#64748b';
export const BG_SUBTLE = '#f8fafc';

export interface AdminHomeDashboardTileProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
  iconBgColor?: string;
  badgeCount?: number;
  /** Dark teal featured tile (Surplus Lines Tax Estimator mockup) — text only, no icon */
  featured?: boolean;
}

export const AdminHomeDashboardTile: React.FC<AdminHomeDashboardTileProps> = ({
  icon,
  title,
  subtitle,
  onClick,
  iconBgColor = '#f0f9ff',
  badgeCount,
  featured = false,
}) => {
  if (featured) {
    return (
      <Box
        onClick={onClick}
        sx={{
          backgroundColor: '#3a6572',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          minHeight: '180px',
          padding: '24px',
          border: '1px solid #1e4a56',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
          '&:hover': {
            backgroundColor: '#2f535e',
            boxShadow: '0 4px 12px rgba(30, 74, 86, 0.25)',
          },
        }}
      >
        <Typography
          sx={{
            color: '#ffffff',
            fontSize: '1rem',
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.35,
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  const iconBox = (
    <Box
      sx={{
        width: '64px',
        height: '64px',
        borderRadius: '12px',
        backgroundColor: iconBgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      {icon}
    </Box>
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        minHeight: '180px',
        padding: '24px',
        border: `1px solid ${BORDER}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        '&:hover': {
          borderColor: ACCENT,
          backgroundColor: ACCENT_LIGHT,
          boxShadow: '0 4px 12px rgba(32, 153, 210, 0.12)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {badgeCount != null && badgeCount > 0 ? (
          <Badge badgeContent={badgeCount} color="error">
            {iconBox}
          </Badge>
        ) : (
          iconBox
        )}
      </Box>
      <Typography
        sx={{
          color: TEXT_PRIMARY,
          fontSize: '1rem',
          fontWeight: 600,
          textAlign: 'center',
          mb: subtitle ? 0.5 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            color: TEXT_SECONDARY,
            fontSize: '0.8125rem',
            fontWeight: 400,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};
